import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { NoAccess } from "@/components/NoAccess";
import { GuideSheet } from "@/components/kit/GuideSheet";
import { ShoppingProgress } from "@/components/kit/ShoppingBits";
import { TabRow } from "@/components/kit/TabRow";
import { LockedFeatureCard } from "@/components/kit/LockedFeatureCard";
import { PageBand } from "@/components/kit/PageBand";
import { cn } from "@/lib/utils";
import { api, routes } from "@/lib/ppcp";
import { ShoppingProvider, useShopping } from "@/lib/shopping";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { KIT, PAGES } from "@/constants/testIds";

/** Guides are chapters too: a teal band, then one white panel per section. */
const BAND = {
  grocery: {
    accent: "Grocery",
    intro:
      "Everything you need to buy, split the way the plan actually cooks: staples once a month, fresh food week by week.",
  },
  protein: {
    accent: "Protein",
    intro:
      "Your meal plan already works on its own. This chapter explains the protein behind it, so you can adjust with confidence.",
  },
};

/** A section titled "Master Pantry List — Dals, Grains, Nuts & Oils" reads as two things: the shelf it
 *  belongs to and the category itself. Splitting on the authored dash gives the category its own line,
 *  so any guide's categories are scannable without a single name living in code. */
const splitTitle = (title = "") => {
  const at = title.search(/\s[—–]\s/);
  return at === -1
    ? { label: null, name: title }
    : { label: title.slice(0, at).trim(), name: title.slice(at + 3).trim() };
};

const Panels = ({ sections = [] }) =>
  sections.map((section) => {
    const { label, name } = splitTitle(section.title);
    const hasTable = (section.blocks ?? []).some((b) => b.type === "table");
    return (
      <section
        key={section.key}
        data-testid={KIT.guideSection(section.key)}
        className={cn("ps-card", hasTable ? "ps-card-pad-table" : "ps-card-pad")}
      >
        {label && (
          <p data-testid={`guide-section-label-${section.key}`} className="ps-eyebrow mb-2">
            {label}
          </p>
        )}
        <GuideSheet blocks={[{ type: "heading", text: name }, ...(section.blocks ?? [])]} />
      </section>
    );
  });

/** A shopping list: the same panel, plus a tick beside every item and a count of what's left.
 *  One list per section, so Week 1's ticks and the monthly staples never share state. */
const ShoppingPanels = ({ sections = [] }) => {
  const shopping = useShopping();

  /** Clearing is instant but never final: the toast hands the list straight back. */
  const clearList = async (sectionKey, bought) => {
    const cleared = await shopping.clear(sectionKey);
    if (!cleared.length) return;
    track("shopping_list_cleared", { list: sectionKey, items_cleared: bought });
    toast(`Cleared ${cleared.length} ${cleared.length === 1 ? "item" : "items"}`, {
      duration: 6000,
      action: { label: "Undo", onClick: () => shopping.restore(cleared) },
    });
  };

  return sections.map((section) => {
    const { label, name } = splitTitle(section.title);
    const ids = (section.blocks ?? []).flatMap((b) => b.item_ids ?? []);
    const bought = ids.filter((id) => shopping.checked.includes(id)).length;

    return (
      <section
        key={section.key}
        data-testid={KIT.guideSection(section.key)}
        className="ps-card ps-card-pad-table"
      >
        {label && (
          <p data-testid={`guide-section-label-${section.key}`} className="ps-eyebrow mb-2">
            {label}
          </p>
        )}
        <GuideSheet
          blocks={[{ type: "heading", text: name }, ...(section.blocks ?? [])]}
          shopping={shopping}
        />
        {!!ids.length && (
          <div className="mt-7">
            <ShoppingProgress
              testId={KIT.shopProgress(section.key)}
              bought={bought}
              total={ids.length}
              onClear={() => clearList(section.key, bought)}
            />
          </div>
        )}
      </section>
    );
  });
};

/** Tab order follows the guide's own reading order; Weekly Fresh is the landing tab because it is
 *  the list you actually stand in a shop with. */
const GROCERY_TABS = [
  { id: "how", label: "How to Use", testId: "grocery-tab-how" },
  { id: "master", label: "Monthly Staples", testId: "grocery-tab-master" },
  { id: "weekly", label: "Weekly Fresh", testId: "grocery-tab-weekly" },
];

/** Reference material, in the order the publisher's guide reads. */
const HOW_TO_USE_ORDER = [
  "how-to-shop",
  "light-sabzi-options",
  "convenience-swaps",
  "storage-and-prep",
  "serving-sizes",
];

export default function GuidePage() {
  const { slug, guideKey } = useParams();
  // Ticks belong to the Grocery Guide alone, so no other chapter asks the server for them.
  return (
    <ShoppingProvider slug={slug} enabled={guideKey === "grocery"}>
      <Guide />
    </ShoppingProvider>
  );
}

function Guide() {
  const { slug, guideKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [search] = useSearchParams();
  const currentDay = Number(search.get("day")) || 1;
  const { upgrades } = useAuth();

  const [guide, setGuide] = useState(null);
  const [grocery, setGrocery] = useState(null);
  const [week, setWeek] = useState(null);
  // Always Weekly Fresh on arrival: the last-used tab is deliberately not remembered.
  const [tab, setTab] = useState("weekly");
  const [locked, setLocked] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setGuide(null);
    setGrocery(null);
    setWeek(null);
    setTab("weekly");
    setLocked(null);
    api
      .get(`/programs/${slug}/guides/${guideKey}`)
      .then((res) => setGuide(res.data))
      .catch((err) => {
        if (err.response?.status === 403) return setLocked(err.response.data?.detail ?? true);
        if (err.response?.status === 401) return setDenied(true);
        // A guide a plan no longer ships (the retired protein companion) belongs nowhere: send the
        // reader to the plan rather than to a dead page.
        if (err.response?.status === 404) return navigate(routes.program(slug), { replace: true });
        setGuide(false);
      });
  }, [slug, guideKey]);

  // Every fresh arrival lands on Weekly Fresh — including a Back/Forward return to the same URL,
  // which keeps the route params identical and so would not re-run the fetch effect above.
  useEffect(() => {
    setTab("weekly");
  }, [location.key]);

  useEffect(() => {
    if (guideKey !== "grocery" || !guide) return;
    api
      .get(`/programs/${slug}/grocery`, { params: { current_day: currentDay, ...(week ? { week } : {}) } })
      .then((res) => {
        setGrocery(res.data);
        setWeek(res.data.active_week);
      })
      .catch(() => setGrocery(false));
  }, [slug, guideKey, guide, currentDay, week]);

  if (denied) return <NoAccess title="Guide" />;

  const isGrocery = guideKey === "grocery";
  const band = BAND[guideKey] ?? {};
  const title = guide?.title ?? (isGrocery ? "Grocery Guide" : "Guide");

  return (
    <AppShell
      title={title}
      slug={slug}
      size="reading"
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: "Plan Overview", to: routes.program(slug) },
        { label: title },
      ]}
    >
      <div data-testid={PAGES.guide} className="contents">
        {/* Plan Overview lives in the persistent bottom navigation, so it is not repeated here. */}
        {locked ? (
          <LockedFeatureCard
            featureKey={guideKey}
            title="Part of the Complete Meal Plan"
            description={typeof locked === "string" ? locked : "This guide is part of the Complete Meal Plan."}
            upgradeHref={upgrades?.[slug]}
            upgradeLabel="Upgrade to Complete Plan →"
          />
        ) : guide === false ? (
          <LockedFeatureCard
            featureKey={guideKey}
            title="Guide not available"
            description="This guide has not been published for this program yet."
          />
        ) : (
          <>
            <PageBand eyebrow="Complete Meal Plan chapter" title={title} accent={band.accent} intro={band.intro} />

            {isGrocery && grocery ? (
              <>
                {/* Three tabs, so each kind of information has one obvious home. Weekly Fresh is
                    always the landing tab — the list you shop from. */}
                <TabRow
                  testId="grocery-tabs"
                  label="Grocery Guide sections"
                  tabs={GROCERY_TABS}
                  active={tab}
                  onChange={setTab}
                />

                {tab === "weekly" && (
                  <>
                    {/* Only the selected week is shown; each week keeps its own ticks. */}
                    <div className="space-y-4">
                      <p className="ps-eyebrow">Your week</p>
                      <TabRow
                        size="sm"
                        testId="grocery-week-tabs"
                        label="Shopping week"
                        tabs={grocery.weeks.map((w) => ({
                          id: w,
                          label: `Week ${w}`,
                          testId: KIT.guideWeekTab(w),
                        }))}
                        active={week ?? grocery.active_week}
                        onChange={setWeek}
                      />
                    </div>
                    <ShoppingPanels sections={grocery.current} />
                  </>
                )}

                {tab === "master" && <ShoppingPanels sections={grocery.master} />}

                {tab === "how" && (
                  <Panels
                    sections={HOW_TO_USE_ORDER.map((key) =>
                      grocery.support.find((s) => s.key === key)
                    ).filter(Boolean)}
                  />
                )}
              </>
            ) : (
              <Panels sections={guide?.sections ?? []} />
            )}

            {/* The chapter simply ends: Plan Overview and the Day Index are in the bottom navigation. */}
          </>
        )}
      </div>
    </AppShell>
  );
}
